import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';
import { settings } from '@/config';
import { getColor, getItemFromStore } from '@/common/utils';
import { configReducer } from '@/reducers/configReducer';
import { isAuthConfigured, msalConfig } from '@/authConfig';
import {
  AuthenticationResult,
  EventType,
  PublicClientApplication
} from '@azure/msal-browser';
import { B2CIdTokenClaims, UserRole } from '@/types/user';
import { getUserData } from '@/common/utils/auth';

export const msalInstance = new PublicClientApplication(msalConfig);

if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

const configState = {
  navbarPosition: getItemFromStore('navbarPosition', settings.navbarPosition),
  isNavbarVerticalCollapsed: getItemFromStore(
    'isNavbarVerticalCollapsed',
    settings.isNavbarVerticalCollapsed
  ),
  openModal: false,
  showBurgerMenu: settings.showBurgerMenu
};

interface AppContextType {
  config: typeof configState;
  setConfig: (key: string, value: any) => void;
  configDispatch: React.Dispatch<any>;
  getThemeColor: (name: string) => string;
  msalInstance: PublicClientApplication;
  userRole: UserRole;
  userId: string;
}

export const AppContext = createContext<AppContextType>({
  config: {
    ...settings,
    openModal: false
  },
  setConfig: () => {},
  configDispatch: () => {},
  getThemeColor: () => '',
  msalInstance: msalInstance,
  userRole: UserRole.B2C,
  userId: ''
});

interface AppProviderProps {
  children: ReactNode;
}

const AppProvider: FC<AppProviderProps> = ({ children }) => {
  const [config, configDispatch] = useReducer(configReducer, configState);
  const [userData, setUserData] = useState<B2CIdTokenClaims | null>(null);

  const fetchUserData = async () => {
    if (!isAuthConfigured || !msalInstance.getActiveAccount()) {
      setUserData(null);
      return;
    }

    try {
      const data = await getUserData(msalInstance);
      setUserData(data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    if (!isAuthConfigured) {
      return;
    }

    fetchUserData();
  }, []);

  useEffect(() => {
    if (!isAuthConfigured) {
      return;
    }

    const callbackId = msalInstance.addEventCallback(async event => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const account = (event.payload as AuthenticationResult | null)?.account;

        if (account) {
          msalInstance.setActiveAccount(account);
        }

        await fetchUserData();
      } else if (
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS
      ) {
        await fetchUserData();
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setUserData(null);
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  const setConfig = (key: string, value: any) => {
    configDispatch({
      type: 'SET_CONFIG',
      payload: {
        key,
        value,
        setInStore: ['isNavbarVerticalCollapsed', 'navbarPosition'].includes(
          key
        )
      }
    });
  };

  const userRole = userData?.extension_Role as UserRole;
  const userId = userData?.extension_CustomerB2BId as string;

  const getThemeColor = (name: string) => getColor(name);
  
  return (
    <AppContext.Provider
      value={{
        config,
        setConfig,
        configDispatch,
        getThemeColor,
        msalInstance,
        userRole,
        userId
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

export default AppProvider;
